<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { useUserStore } from '../../stores/user'
import { formatDate } from '../../utils/format'

const CONFIRM_WORD = 'DELETE'
const DELETION_WINDOW_DAYS = 45

const $q = useQuasar()
const userStore = useUserStore()
const { accountDeletion } = storeToRefs(userStore)

const dialogOpen = ref(false)
const typed = ref('')
const busy = ref(false)

const canConfirm = computed(() => typed.value === CONFIRM_WORD)
const deleteBefore = computed(() => formatDate(accountDeletion.value?.deleteBefore))

function openDialog() {
  typed.value = ''
  dialogOpen.value = true
}

function notifyFailure(message: string) {
  $q.notify({ color: 'negative', textColor: 'white', icon: 'mdi-alert', message })
}

async function confirmDeletion() {
  if (!canConfirm.value || busy.value) return
  busy.value = true
  try {
    await userStore.requestAccountDeletion()
    dialogOpen.value = false
  } catch (e) {
    console.error('Failed to request account deletion', e)
    notifyFailure('Could not request account deletion. Please try again.')
  } finally {
    busy.value = false
  }
}

async function cancelDeletion() {
  if (busy.value) return
  busy.value = true
  try {
    await userStore.cancelAccountDeletion()
    $q.notify({
      color: 'positive',
      textColor: 'white',
      icon: 'mdi-check',
      message: 'Account deletion cancelled.'
    })
  } catch (e) {
    console.error('Failed to cancel account deletion', e)
    notifyFailure('Could not cancel account deletion. Please try again.')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <q-card flat bordered id="delete-account" class="settings-card" data-testid="delete-account-card">
    <q-card-section>
      <div class="text-h6">Delete account</div>
    </q-card-section>

    <q-card-section v-if="accountDeletion" class="column items-start q-gutter-sm">
      <q-banner rounded class="bg-orange-1 text-dark full-width" data-testid="delete-pending">
        <template #avatar><q-icon name="mdi-alert" color="warning" /></template>
        Your account will be deleted by {{ deleteBefore }}.
      </q-banner>
      <q-btn
        outline
        color="primary"
        label="Cancel deletion"
        :loading="busy"
        data-testid="cancel-deletion-button"
        @click="cancelDeletion"
      />
    </q-card-section>

    <q-card-section v-else class="column items-start q-gutter-sm">
      <div>We delete your data within {{ DELETION_WINDOW_DAYS }} days.</div>
      <q-btn
        outline
        color="negative"
        label="Delete my account"
        data-testid="delete-account-button"
        @click="openDialog"
      />
    </q-card-section>

    <q-dialog v-model="dialogOpen" :persistent="busy">
      <q-card class="delete-dialog" data-testid="delete-dialog">
        <q-card-section>
          <div class="text-h6">Delete your account?</div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <p>
            Your profile, reports and points will be deleted within {{ DELETION_WINDOW_DAYS }} days.
            You can cancel before then.
          </p>
          <q-input
            v-model="typed"
            outlined
            :label="`Type ${CONFIRM_WORD} to confirm`"
            autocomplete="off"
            data-testid="delete-confirm-input"
            @keyup.enter="confirmDeletion"
          />
        </q-card-section>
        <q-card-actions vertical class="q-pa-md q-gutter-sm">
          <q-btn
            unelevated
            color="negative"
            label="Delete account"
            :disable="!canConfirm"
            :loading="busy"
            data-testid="delete-confirm-button"
            @click="confirmDeletion"
          />
          <q-btn
            outline
            color="primary"
            label="Keep my account"
            :disable="busy"
            data-testid="delete-keep-button"
            @click="dialogOpen = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<style scoped>
.delete-dialog {
  width: 100%;
  max-width: 420px;
}
</style>
